/**
 * YouTube helpers shared across the SMA Digital Lab resource experience.
 *
 * The same extraction logic lives server-side in resourceLibraryService; this
 * client helper is used for live form validation/preview and for the student
 * viewer. Keep the two in sync.
 */

// Extracts the YouTube video id from any common URL form:
//   youtube.com/watch?v=ID, youtu.be/ID, youtube.com/shorts/ID,
//   youtube.com/embed/ID, m.youtube.com/watch?v=ID
// Also accepts a bare id. Returns null when no id can be reliably extracted.
export function extractYouTubeId(value) {
  if (!value) return null
  const raw = String(value).trim()
  if (!raw) return null

  if (/^[A-Za-z0-9_-]{6,}$/.test(raw) && !raw.includes('.') && !raw.includes('/')) {
    return raw
  }

  try {
    const parsed = new URL(raw.startsWith('http') ? raw : `https://${raw}`)
    const host = parsed.hostname.replace(/^www\./, '').replace(/^m\./, '')
    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0]
      return id && /^[A-Za-z0-9_-]{6,}$/.test(id) ? id : null
    }
    if (host === 'youtube.com' || host === 'music.youtube.com') {
      if (parsed.pathname === '/watch') {
        const v = parsed.searchParams.get('v')
        return v && /^[A-Za-z0-9_-]{6,}$/.test(v) ? v : null
      }
      const parts = parsed.pathname.split('/').filter(Boolean)
      if ((parts[0] === 'shorts' || parts[0] === 'embed') && parts[1]) {
        return /^[A-Za-z0-9_-]{6,}$/.test(parts[1]) ? parts[1] : null
      }
    }
  } catch (_) {
    // fall through
  }
  return null
}

export function isYouTubeUrl(value) {
  return extractYouTubeId(value) !== null
}

export function youTubeWatchUrl(id) {
  return `https://www.youtube.com/watch?v=${id}`
}

export function youTubeEmbedUrl(id) {
  // rel=0: don't show unrelated related videos at the end
  // modestbranding=1: lighter YouTube branding
  return `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`
}

export function youTubeThumbnail(id) {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
}
